/**
 * Browser push, from the app's side.
 *
 * The browser does three things for us: it registers the service worker in
 * `public/sw.js`, it asks the person once whether the site may notify them,
 * and it hands back a subscription — an endpoint at the browser vendor's push
 * service and two keys. The API stores that and sends to it; the worker shows
 * what arrives.
 */

export type PushState = 'unsupported' | NotificationPermission;

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission(): PushState {
  return pushSupported() ? Notification.permission : 'unsupported';
}

/** A VAPID key is URL-safe base64; `subscribe` wants the raw bytes. */
function keyBytes(base64Url: string): ArrayBuffer {
  const padded = base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buf;
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Asks, registers, subscribes. Throws when the person said no. */
export async function enablePush(publicKey: string): Promise<PushSubscription> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('denied');
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(publicKey),
  });
}

/** Withdraws the browser's subscription; returns the endpoint it had. */
export async function disablePush(): Promise<string | null> {
  const sub = await currentPushSubscription();
  if (!sub) return null;
  await sub.unsubscribe();
  return sub.endpoint;
}

/** What the API accepts — the subscription's JSON and nothing extra. */
export function subscriptionBody(sub: PushSubscription) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint ?? sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
}
